import React, { useState } from 'react';
import { Button, DatePicker, Input, Radio, Spin, Table, Tabs } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { useN301Query } from './hooks/useN301Query';
import type { N301ReportRow, N301SummaryRow } from '../../services/n301Api';
import './N301Form.css';

const detailColumns: ColumnsType<N301ReportRow> = [
  {
    title: '客訴單號',
    dataIndex: 'fn110_01',
    key: 'fn110_01',
    width: 100,
    sorter: (a, b) => a.fn110_01.localeCompare(b.fn110_01),
  },
  {
    title: '客訴日期',
    dataIndex: 'fn110_11',
    key: 'fn110_11',
    width: 110,
    render: (v: string | null) => v ? dayjs(v).format('YYYY-MM-DD') : '-',
    sorter: (a, b) => (a.fn110_11 || '').localeCompare(b.fn110_11 || ''),
  },
  {
    title: '客戶代號',
    dataIndex: 'fn110_12',
    key: 'fn110_12',
    width: 110,
    sorter: (a, b) => a.fn110_12.localeCompare(b.fn110_12),
  },
  {
    title: '客戶名稱',
    dataIndex: 'customer_name',
    key: 'customer_name',
    width: 150,
    ellipsis: true,
  },
  {
    title: '部門',
    dataIndex: 'fn110_22',
    key: 'fn110_22',
    width: 60,
  },
  {
    title: '部門名稱',
    dataIndex: 'dept_name',
    key: 'dept_name',
    width: 100,
    ellipsis: true,
  },
  {
    title: '原因代號',
    dataIndex: 'fn112_11',
    key: 'fn112_11',
    width: 80,
    sorter: (a, b) => a.fn112_11.localeCompare(b.fn112_11),
  },
  {
    title: '原因名稱',
    dataIndex: 'cause_name',
    key: 'cause_name',
    width: 120,
    ellipsis: true,
  },
  {
    title: '費率',
    dataIndex: 'fn112_13',
    key: 'fn112_13',
    width: 90,
    align: 'right' as const,
    render: (v: number) => v.toFixed(4),
    sorter: (a, b) => a.fn112_13 - b.fn112_13,
  },
  {
    title: '金額',
    dataIndex: 'fn112_14',
    key: 'fn112_14',
    width: 100,
    align: 'right' as const,
    render: (v: number) => v.toFixed(2),
    sorter: (a, b) => a.fn112_14 - b.fn112_14,
  },
  {
    title: '計算金額',
    dataIndex: 'fn112_amt',
    key: 'fn112_amt',
    width: 110,
    align: 'right' as const,
    render: (v: number) => {
      const color = v < 0 ? '#f5222d' : '#262626';
      return <span style={{ color, fontWeight: v !== 0 ? 600 : 400 }}>{v.toFixed(0)}</span>;
    },
    sorter: (a, b) => a.fn112_amt - b.fn112_amt,
  },
  {
    title: '狀態',
    dataIndex: 'status_text',
    key: 'status_text',
    width: 70,
    render: (v: string) => (
      <span style={{ color: v === '已結案' ? '#52c41a' : '#faad14' }}>{v}</span>
    ),
  },
];

const summaryColumns: ColumnsType<N301SummaryRow> = [
  {
    title: '代號',
    dataIndex: 'group_key',
    key: 'group_key',
    width: 120,
    sorter: (a, b) => a.group_key.localeCompare(b.group_key),
  },
  {
    title: '名稱',
    dataIndex: 'group_name',
    key: 'group_name',
    width: 200,
    ellipsis: true,
  },
  {
    title: '合計金額',
    dataIndex: 'total_amt',
    key: 'total_amt',
    width: 130,
    align: 'right' as const,
    render: (v: number) => v.toFixed(0),
    sorter: (a, b) => a.total_amt - b.total_amt,
  },
  {
    title: '筆數',
    dataIndex: 'record_count',
    key: 'record_count',
    width: 80,
    align: 'right' as const,
    sorter: (a, b) => a.record_count - b.record_count,
  },
];

const N301Form: React.FC = () => {
  const [activeTab, setActiveTab] = useState('detail');
  const {
    loading, records, customerSummary, causeSummary, total, filterDesc,
    startDate, setStartDate,
    lastDate, setLastDate,
    startCause, setStartCause, handleStartCauseBlur,
    lastCause, setLastCause,
    startCustomer, setStartCustomer, handleStartCustomerBlur,
    lastCustomer, setLastCustomer,
    startDept, setStartDept, handleStartDeptBlur,
    lastDept, setLastDept,
    caseStatus, setCaseStatus,
    handleQuery,
  } = useN301Query();

  return (
    <Spin spinning={loading}>
      <div className="n301-form">
        <h2 style={{ marginBottom: 16 }}>
          客訴原因明細表 -- pwn301f1
          {total > 0 && (
            <span style={{ marginLeft: 12, fontSize: 14, fontWeight: 400, color: '#595959' }}>
              共 {total} 筆
            </span>
          )}
        </h2>

        <div className="filter-section">
          {/* BR-F01: Department range */}
          <div className="filter-row">
            <span className="filter-label">部門代號:</span>
            <Input
              placeholder="[從]"
              value={startDept}
              onChange={(e) => setStartDept(e.target.value)}
              onBlur={handleStartDeptBlur}
              maxLength={4}
              style={{ width: 120 }}
            />
            <span className="filter-sep">~</span>
            <Input
              placeholder="[至]"
              value={lastDept}
              onChange={(e) => setLastDept(e.target.value)}
              maxLength={4}
              style={{ width: 120 }}
            />
          </div>

          {/* Date range */}
          <div className="filter-row">
            <span className="filter-label">客訴日期:</span>
            <DatePicker
              placeholder="[從]"
              value={startDate ? dayjs(startDate) : null}
              onChange={(d) => setStartDate(d ? d.format('YYYY-MM-DD') : '')}
              style={{ width: 140 }}
            />
            <span className="filter-sep">~</span>
            <DatePicker
              placeholder="[至]"
              value={lastDate ? dayjs(lastDate) : null}
              onChange={(d) => setLastDate(d ? d.format('YYYY-MM-DD') : '')}
              style={{ width: 140 }}
            />
          </div>

          {/* BR-F04: Cause code range */}
          <div className="filter-row">
            <span className="filter-label">客訴處理要求/原因代號:</span>
            <Input
              placeholder="[從]"
              value={startCause}
              onChange={(e) => setStartCause(e.target.value)}
              onBlur={handleStartCauseBlur}
              maxLength={3}
              style={{ width: 120 }}
            />
            <span className="filter-sep">~</span>
            <Input
              placeholder="[至]"
              value={lastCause}
              onChange={(e) => setLastCause(e.target.value)}
              maxLength={3}
              style={{ width: 120 }}
            />
          </div>

          {/* BR-F07: Customer/vendor range */}
          <div className="filter-row">
            <span className="filter-label">客戶/廠商代號:</span>
            <Input
              placeholder="[從]"
              value={startCustomer}
              onChange={(e) => setStartCustomer(e.target.value)}
              onBlur={handleStartCustomerBlur}
              maxLength={12}
              style={{ width: 160 }}
            />
            <span className="filter-sep">~</span>
            <Input
              placeholder="[至]"
              value={lastCustomer}
              onChange={(e) => setLastCustomer(e.target.value)}
              maxLength={12}
              style={{ width: 160 }}
            />
          </div>

          {/* BR-P04: Case status filter */}
          <div className="filter-row">
            <span className="filter-label">客訴單據類別:</span>
            <Radio.Group
              value={caseStatus}
              onChange={(e) => setCaseStatus(e.target.value)}
            >
              <Radio value={1}>已結案</Radio>
              <Radio value={2}>未結案</Radio>
              <Radio value={3}>全部</Radio>
            </Radio.Group>
          </div>
        </div>

        {/* Query button */}
        <div className="action-row">
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleQuery}
            loading={loading}
          >
            開始查詢
          </Button>
          {filterDesc && (
            <span className="filter-desc">{filterDesc}</span>
          )}
        </div>

        {/* Results tabs */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'detail',
              label: `明細 (${total})`,
              children: (
                <Table<N301ReportRow>
                  columns={detailColumns}
                  dataSource={records}
                  rowKey={(r) => `${r.fn110_01}-${r.fn112_02}`}
                  pagination={{
                    pageSize: 100,
                    showSizeChanger: true,
                    showTotal: (t) => `共 ${t} 筆`,
                  }}
                  size="small"
                  scroll={{ x: 1400 }}
                  locale={{ emptyText: '請設定篩選條件後按「開始查詢」' }}
                />
              ),
            },
            {
              key: 'customer',
              label: `客戶彙總 (${customerSummary.length})`,
              children: (
                <Table<N301SummaryRow>
                  columns={summaryColumns}
                  dataSource={customerSummary}
                  rowKey={(r) => r.group_key}
                  pagination={false}
                  size="small"
                  scroll={{ x: 530 }}
                  locale={{ emptyText: '尚無客戶彙總資料' }}
                />
              ),
            },
            {
              key: 'cause',
              label: `原因彙總 (${causeSummary.length})`,
              children: (
                <Table<N301SummaryRow>
                  columns={summaryColumns}
                  dataSource={causeSummary}
                  rowKey={(r) => r.group_key}
                  pagination={false}
                  size="small"
                  scroll={{ x: 530 }}
                  locale={{ emptyText: '尚無原因彙總資料' }}
                />
              ),
            },
          ]}
        />
      </div>
    </Spin>
  );
};

export default N301Form;
